import { Controller } from '@hotwired/stimulus'

// Type-to-filter for every <select> with more than MAX_PLAIN options, delegated from <body> so forms, filter cards,
// frames and the modal need no markup. The native <select> stays visible and is the source of truth (same params,
// Playwright selectOption works): opening it (click, Enter/Space/↑/↓/F4 or typing) shows a popover with a search box
// and the select's current, not hidden options — so option_filter (CAT-017 hotels by city) is honoured as is.
// Matching ignores case and diacritics („ceske“ finds „České“). Opt out with data-searchable="false".
const MAX_PLAIN = 8
const MAX_SHOWN = 300 // ponytail: render cap for huge lists; typing narrows them anyway
const OPEN_KEYS = new Set(['Enter', ' ', 'ArrowDown', 'ArrowUp', 'F4'])

const fold = (text: string): string => text.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase()

let sequence = 0

export default class SearchableSelectController extends Controller<HTMLElement> {
  static override values = { placeholder: String, empty: String }
  declare readonly placeholderValue: string
  declare readonly emptyValue: string

  private select: HTMLSelectElement | null = null
  private popover: HTMLElement | null = null
  private input: HTMLInputElement | null = null
  private list: HTMLElement | null = null
  private active = -1
  private shown: HTMLOptionElement[] = []

  override connect(): void {
    this.element.addEventListener('mousedown', this.onMouseDown)
    this.element.addEventListener('keydown', this.onKeyDown)
    document.addEventListener('turbo:before-cache', this.close)
    window.addEventListener('resize', this.close)
    document.addEventListener('scroll', this.onScroll, true)
  }

  override disconnect(): void {
    this.close()
    this.element.removeEventListener('mousedown', this.onMouseDown)
    this.element.removeEventListener('keydown', this.onKeyDown)
    document.removeEventListener('turbo:before-cache', this.close)
    window.removeEventListener('resize', this.close)
    document.removeEventListener('scroll', this.onScroll, true)
  }

  private searchable(target: EventTarget | null): HTMLSelectElement | null {
    if (!(target instanceof HTMLSelectElement)) return null
    const plain = target.multiple || target.size > 1 || target.matches(':disabled') || target.dataset['searchable'] === 'false'
    return plain || target.options.length <= MAX_PLAIN ? null : target
  }

  private readonly onMouseDown = (event: MouseEvent): void => {
    const select = this.searchable(event.target)
    if (!select || event.button !== 0) return
    event.preventDefault() // no native dropdown
    if (this.select === select) return this.close()
    select.focus()
    this.open(select, '')
  }

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    const select = this.searchable(event.target)
    if (!select || event.ctrlKey || event.metaKey) return
    const typed = event.key.length === 1 && event.key !== ' ' && !event.altKey
    if (!typed && !OPEN_KEYS.has(event.key)) return
    event.preventDefault()
    this.open(select, typed ? event.key : '')
  }

  private readonly onScroll = (event: Event): void => {
    if (this.popover && !(event.target instanceof Node && this.popover.contains(event.target))) this.close()
  }

  private open(select: HTMLSelectElement, query: string): void {
    this.close()
    const id = `searchable-select-${++sequence}`
    const popover = document.createElement('div')
    popover.setAttribute('popover', 'manual')
    popover.className = 'm-0 w-auto rounded-box border border-base-300 bg-base-100 p-2 text-base-content shadow-lg'
    popover.innerHTML = `<input type="search" class="input input-sm w-full" autocomplete="off" role="combobox"
      aria-autocomplete="list" aria-expanded="true" aria-controls="${id}">
      <ul role="listbox" id="${id}" class="mt-1 max-h-72 overflow-y-auto"></ul>`
    const input = popover.querySelector('input') as HTMLInputElement
    input.placeholder = this.placeholderValue
    input.setAttribute('aria-label', this.placeholderValue)
    input.value = query
    // Right after the select: inside the same (modal) dialog, so not inert, and Tab goes on to the next field.
    select.after(popover)
    Object.assign(this, { select, popover, input, list: popover.querySelector('ul') })

    input.addEventListener('input', (event) => {
      event.stopPropagation() // searching is not editing the form (dirty-form)
      this.render()
    })
    input.addEventListener('change', (event) => event.stopPropagation())
    input.addEventListener('keydown', this.onInputKey)
    popover.addEventListener('focusout', (event) => {
      if (!(event.relatedTarget instanceof Node && popover.contains(event.relatedTarget))) this.close()
    })
    popover.addEventListener('mousedown', (event) => {
      if (event.target !== input) event.preventDefault() // keep focus in the search box
      const option = (event.target as Element).closest<HTMLElement>('[role=option]')
      if (option) this.choose(Number(option.dataset['index']))
    })

    popover.showPopover()
    this.place()
    this.render()
    input.focus()
  }

  private place(): void {
    if (!this.select || !this.popover) return
    const box = this.select.getBoundingClientRect()
    const style = this.popover.style
    style.position = 'fixed'
    style.inset = 'auto'
    style.left = `${Math.max(0, Math.min(box.left, window.innerWidth - Math.max(box.width, 256)))}px`
    style.width = `${Math.max(box.width, 256)}px`
    const below = window.innerHeight - box.bottom
    if (below < 360 && box.top > below) style.bottom = `${window.innerHeight - box.top + 2}px`
    else style.top = `${box.bottom + 2}px`
  }

  private render(): void {
    if (!this.select || !this.list || !this.input) return
    const terms = fold(this.input.value).split(/\s+/).filter(Boolean)
    const options = Array.from(this.select.options).filter((option) => !option.hidden && !option.disabled)
    this.shown = options
      .filter((option) => (terms.length === 0 ? true : option.value !== '' && terms.every((term) => fold(option.text).includes(term))))
      .slice(0, MAX_SHOWN)
    const current = this.shown.findIndex((option) => option.selected)
    this.active = terms.length === 0 ? Math.max(current, 0) : 0
    this.list.replaceChildren(
      ...this.shown.map((option, index) => {
        const item = document.createElement('li')
        item.id = `${this.list?.id}-${index}`
        item.setAttribute('role', 'option')
        item.dataset['index'] = String(index)
        item.className = 'cursor-pointer rounded-field px-2 py-1 text-sm aria-selected:bg-primary aria-selected:text-primary-content'
        if (option.selected) item.classList.add('font-semibold')
        item.textContent = option.text.trim() || '—'
        return item
      }),
    )
    if (this.shown.length === 0) {
      const empty = document.createElement('li')
      empty.className = 'px-2 py-1 text-sm opacity-60'
      empty.textContent = this.emptyValue
      this.list.append(empty)
    }
    this.highlight()
  }

  private highlight(): void {
    const items = this.list?.querySelectorAll<HTMLElement>('[role=option]') ?? []
    items.forEach((item, index) => item.setAttribute('aria-selected', String(index === this.active)))
    const item = items[this.active]
    if (item) {
      this.input?.setAttribute('aria-activedescendant', item.id)
      item.scrollIntoView({ block: 'nearest' })
    } else this.input?.removeAttribute('aria-activedescendant')
  }

  private readonly onInputKey = (event: KeyboardEvent): void => {
    const last = this.shown.length - 1
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      this.active = Math.min(Math.max(this.active + (event.key === 'ArrowDown' ? 1 : -1), 0), last)
      this.highlight()
    } else if (event.key === 'Home' || event.key === 'End') {
      if (this.input?.value) return // in a typed query they move the caret
      this.active = event.key === 'Home' ? 0 : last
      this.highlight()
    } else if (event.key === 'Enter') {
      if (this.active >= 0) this.choose(this.active)
    } else if (event.key === 'Escape') {
      event.stopPropagation() // not the modal / dialog
      const select = this.select
      this.close()
      select?.focus()
    } else return
    event.preventDefault()
  }

  private choose(index: number): void {
    const option = this.shown[index]
    const select = this.select
    if (!option || !select) return
    const changed = select.value !== option.value
    select.value = option.value
    this.close()
    select.focus()
    if (!changed) return
    select.dispatchEvent(new Event('input', { bubbles: true }))
    select.dispatchEvent(new Event('change', { bubbles: true }))
  }

  private readonly close = (): void => {
    const popover = this.popover
    this.popover = this.input = this.list = this.select = null
    this.shown = []
    popover?.remove()
  }
}
